import { TreeViewItemId } from '@mui/x-tree-view';
import type { ICustomTreeItem, ISelectedTreeItem } from '../../types';

/**
 * Performs search functionality on tree view data
 * Filters features and options based on search value
 *
 * @param {ICustomTreeItem[]} initialData Initial tree view data structure
 * @param {string | null} searchVal Search value to filter by
 * @returns {ICustomTreeItem[]} Filtered tree data matching search criteria
 */
export function getSearchData(
  initialData: ICustomTreeItem[],
  searchVal: string | null
): ICustomTreeItem[] {
  const treeViewData: ICustomTreeItem[] = [];
  if (searchVal) {
    //When search value is present
    const filteredData: ICustomTreeItem[] = [];
    initialData[0].children?.forEach((feature) => {
      //Search on options
      const optionsData = feature.children?.filter((option) =>
        option.label.toLowerCase().includes(searchVal.toLowerCase())
      );
      //Search on Features
      if (
        feature.label.toLowerCase().includes(searchVal.toLowerCase()) ||
        (optionsData && optionsData.length > 0)
      ) {
        filteredData.push({ ...feature, children: optionsData });
      }
    });
    //Update the tree view data with filtered values
    treeViewData.push({
      id: initialData[0].id,
      label: initialData[0].label,
      type: initialData[0].type,
      children: filteredData,
    });
    return treeViewData;
  } else {
    //When search value is cleared
    return initialData;
  }
}

/**
 * Returns all ids of tree items with children (for expand all functionality)
 * Recursively collects IDs of all items that have children
 *
 * @param {ICustomTreeItem[]} filteredData Filtered tree data
 * @returns {TreeViewItemId[]} Array of item IDs that have children
 */
export function getAllItemsWithChildrenIds(filteredData: ICustomTreeItem[]) {
  const itemIds: TreeViewItemId[] = [];
  const registerItemId = (item: ICustomTreeItem) => {
    if (item.children?.length) {
      itemIds.push(item.id);
      item.children.forEach(registerItemId);
    }
  };

  filteredData.forEach(registerItemId);
  return itemIds;
}

/**
 * Find selected option in option data array
 * @param {ICustomTreeItem[]} optionData Options array to search through
 * @param {string} id ID of the selected option
 * @param {string} productCode Product code
 * @param {string} featureCode Feature code
 * @returns Selected option details or null if not found
 */
function getSelectedOption(
  optionData: ICustomTreeItem[],
  id: string,
  productCode: string,
  featureCode: string
) {
  for (const option of optionData) {
    if (option.id === id) {
      //Selected item is option
      return {
        code: option.id.split('.')[1],
        label: option.label,
        type: option.type,
        productCode: productCode,
        featureCode: featureCode,
      };
    }
  }
  return null;
}

/**
 * Find selected item in Features for TreeView
 * Recursively searches through features and their options
 *
 * @param {ICustomTreeItem[]} featureData Features array to search
 * @param {string} id ID of the selected item
 * @param {string} productCode Product code
 * @returns Selected feature details or null if not found
 */
function getSelectedFeature(featureData: ICustomTreeItem[], id: string, productCode: string) {
  for (const feature of featureData) {
    if (feature.id === id) {
      //Selected item is feature
      return {
        code: feature.id,
        label: feature.label,
        type: feature.type,
        productCode: productCode,
      };
    }
    if (feature.children) {
      const selectedOption = getSelectedOption(feature.children, id, productCode, feature.id);
      if (selectedOption) {
        return selectedOption;
      }
    }
  }
  return null;
}

/**
 * Gets the Selected Item for Tree View
 * Returns product, feature, or option details based on selection
 *
 * @param {ICustomTreeItem[]} filteredData Filtered tree data structure
 * @param {string | null} id ID of the selected item
 * @returns {ISelectedTreeItem | null} Selected item details or null if not found
 */
export function getSelectedItem(
  filteredData: ICustomTreeItem[],
  id: string | null
): ISelectedTreeItem | null {
  if (filteredData.length === 0 || !id) {
    return null;
  } else if (id === filteredData[0].id) {
    //Selected item is product
    return {
      code: filteredData[0].id,
      label: filteredData[0].label,
      type: filteredData[0].type,
    };
  } else if (filteredData[0].children) {
    return getSelectedFeature(filteredData[0].children, id, filteredData[0].id);
  }
  return null;
}
